import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api.js';
import { Button, Chip, Banner, Field } from '../components/ui.jsx';
import { Sheet, Divider, Section } from '../spa/SalesPoint.jsx';
import { printElement } from '../lib/print.js';

const card = {
  background: 'var(--color-bg-surface)', border: '1px solid var(--color-border-default)',
  borderRadius: 'var(--radius-lg)', overflow: 'hidden'
};

const ERRORS = {
  username_taken: 'That username is already in use.',
  bad_username: 'Usernames start with a letter and use letters, numbers, dot, dash or underscore. Three characters at least.',
  name_required: 'A full name is required. It is what prints on receipts.',
  at_least_one_business: 'Pick at least one business, or this account signs in to nothing.',
  outranked: 'That account is above your own. You cannot act on it.',
  last_owner_cannot_be_suspended: 'This is the last active owner. Suspending them would lock everybody out.',
  last_owner_cannot_be_demoted: 'This is the last active owner. Demoting them would lock everybody out.',
  cannot_edit_yourself: 'You cannot edit your own account here. Ask another owner or admin.',
  cannot_suspend_yourself: 'You cannot suspend your own account.',
  cannot_reset_yourself: 'You cannot reset your own account here. Use Change password.',
  forbidden: 'Your role does not include this.'
};
const readable = e => ERRORS[e?.data?.error] ||
  (String(e?.data?.error || '').startsWith('not_yours_to_grant')
    ? 'You can only grant a business you hold yourself.'
    : 'That did not work.');

// ---------------------------------------------------------------- the screen

export function Users({ user }) {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ roles: [], assignable: [], grantable: [], holdsAllBusinesses: [], canOnboard: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [onboarding, setOnboarding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [handover, setHandover] = useState(null);
  const [note, setNote] = useState(null);

  const load = () => api.users.list()
    .then(d => { setRows(d.users); setLoading(false); })
    .catch(e => { setError(readable(e)); setLoading(false); });

  useEffect(() => {
    api.users.roles().then(setMeta).catch(() => {});
    load();
  }, []);

  async function act(fn, message) {
    setError(null); setNote(null);
    try {
      const res = await fn();
      await load();
      if (res?.handover) setHandover({ ...res.handover, name: res.user.name, title: 'New credentials' });
      else setNote(message);
    } catch (e) { setError(readable(e)); }
  }

  if (loading) return <div className="t-body-m muted">Loading accounts</div>;

  const byRole = meta.roles.map(r => ({ role: r, people: rows.filter(u => u.role === r.name) }))
    .filter(g => g.people.length);

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16 }}>
        <div style={{ flex: 1 }}>
          <div className="t-body-m secondary">
            Everyone who can sign in. A new account is handed over with a temporary password
            it must replace before it can do anything, so nobody keeps somebody else's password.
          </div>
        </div>
        {meta.canOnboard && <Button onClick={() => setOnboarding(true)}>Onboard someone</Button>}
      </div>

      {error && <Banner tone="danger">{error}</Banner>}
      {note && <Banner tone="success">{note}</Banner>}

      {byRole.map(({ role, people }) => (
        <div key={role.name} style={card}>
          <div style={{ padding: '13px 16px', background: 'var(--color-bg-subtle)', display: 'flex', gap: 10, alignItems: 'baseline' }}>
            <span className="t-label-xs">{role.label.toUpperCase()}{role.subtitle ? `, ${role.subtitle.toUpperCase()}` : ''}</span>
            <span className="t-body-s muted" style={{ flex: 1 }}>{role.blurb}</span>
            <span className="t-body-s muted">{people.length}</span>
          </div>
          {people.map(p => (
            <Person key={p.id} person={p} you={user} meta={meta}
              onEdit={() => setEditing(p)}
              onAct={act} />
          ))}
        </div>
      ))}

      {onboarding && (
        <OnboardSheet meta={meta} onClose={() => setOnboarding(false)}
          onCreated={({ user: created, handover: h }) => {
            setOnboarding(false);
            setHandover({ ...h, name: created.name, title: 'Hand this over' });
            load();
          }} />
      )}

      {editing && (
        <EditSheet person={editing} meta={meta} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}

      {handover && <HandoverCard card={handover} onClose={() => setHandover(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------- one row

function Person({ person, you, meta, onEdit, onAct }) {
  const [open, setOpen] = useState(false);
  const locked = person.locked_until && new Date(person.locked_until) > new Date();
  const isYou = person.id === you.id;
  const reachable = !isYou && meta.assignable.includes(person.role);

  return (
    <div style={{ borderTop: '1px solid var(--color-border-default)' }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '12px 16px' }}>
        <div style={{ width: 220, flex: 'none' }}>
          <div className="t-body-m" style={{ opacity: person.active ? 1 : 0.55 }}>
            {person.name}{isYou && <span className="muted"> (you)</span>}
          </div>
          <div className="t-body-s muted">@{person.username}</div>
        </div>

        <div style={{ flex: 1, minWidth: 0, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {person.businesses.map(b => <Chip key={b.slug} showDot={false}>{b.short_name}</Chip>)}
        </div>

        <div style={{ display: 'flex', gap: 6, flex: 'none' }}>
          {!person.active && <Chip tone="danger">Suspended</Chip>}
          {locked && <Chip tone="warning">Locked out</Chip>}
          {person.must_change_password ? <Chip tone="info">Not yet claimed</Chip> : null}
          {person.mfa_enabled
            ? <Chip tone="success" showDot={false}>2FA</Chip>
            : <Chip tone="warning">No 2FA</Chip>}
        </div>

        <span className="t-body-s muted" style={{ width: 130, flex: 'none', textAlign: 'right' }}>
          {person.last_login
            ? `seen ${new Date(person.last_login).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}`
            : 'never signed in'}
        </span>

        <Button size="m" variant="ghost" style={{ border: '1px solid var(--color-border-default)', flex: 'none' }}
          disabled={!reachable} onClick={() => setOpen(!open)}>
          {open ? 'Close' : 'Manage'}
        </Button>
      </div>

      {open && reachable && (
        <div style={{
          display: 'flex', gap: 8, flexWrap: 'wrap', padding: '0 16px 14px 16px',
          background: 'var(--color-bg-subtle)', paddingTop: 14
        }}>
          <Button size="m" variant="secondary" onClick={onEdit}>Edit role and access</Button>
          <Button size="m" variant="secondary"
            onClick={() => onAct(() => api.users.resetPassword(person.id))}>Reset password</Button>
          <Button size="m" variant="secondary"
            onClick={() => onAct(() => api.users.resetMfa(person.id))}>Re-enrol two factor</Button>
          {locked && (
            <Button size="m" variant="secondary"
              onClick={() => onAct(() => api.users.unlock(person.id), `${person.name} is unlocked.`)}>Unlock</Button>
          )}
          {person.live_sessions > 0 && (
            <Button size="m" variant="secondary"
              onClick={() => onAct(() => api.users.signOutEverywhere(person.id),
                `${person.name} was signed out of ${person.live_sessions} device${person.live_sessions === 1 ? '' : 's'}.`)}>
              Sign out of {person.live_sessions} device{person.live_sessions === 1 ? '' : 's'}
            </Button>
          )}
          <span style={{ flex: 1 }} />
          {person.active
            ? <Button size="m" variant="danger"
                onClick={() => onAct(() => api.users.suspend(person.id), `${person.name} is suspended and signed out.`)}>
                Suspend
              </Button>
            : <Button size="m" variant="secondary"
                onClick={() => onAct(() => api.users.restore(person.id), `${person.name} can sign in again.`)}>
                Restore
              </Button>}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- onboarding

function OnboardSheet({ meta, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '', username: '', email: '', phone: '',
    role: meta.assignable[meta.assignable.length - 1] || 'cashier',
    businesses: meta.grantable.length === 1 ? [meta.grantable[0].slug] : [],
    mfa: false
  });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = patch => setForm(f => ({ ...f, ...patch }));
  const roles = meta.roles.filter(r => meta.assignable.includes(r.name));
  // A rung that holds every business by rung has nothing to tick, so the section is not
  // shown and is not required.
  const needsBusinesses = !meta.holdsAllBusinesses.includes(form.role);
  const ready = form.name.trim() && form.username.trim() &&
    (!needsBusinesses || form.businesses.length) && !busy;

  // Suggest a username from the name, until the person types their own.
  const [touchedUsername, setTouchedUsername] = useState(false);
  const suggest = name => name.trim().toLowerCase().split(/\s+/)[0].replace(/[^a-z0-9]/g, '');

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      onCreated(await api.users.create({ ...form, businesses: needsBusinesses ? form.businesses : [] }));
    } catch (err) { setError(readable(err)); setBusy(false); }
  }

  return (
    <Sheet title="Onboard someone" subtitle="They get a temporary password to replace on first sign in." onClose={onClose}>
      <form onSubmit={submit} style={{ display: 'contents' }}>
        <Section label="WHO">
          <Field id="name" label="FULL NAME" value={form.name} autoFocus
            hint="This prints on receipts as the cashier."
            onChange={e => {
              set({ name: e.target.value });
              if (!touchedUsername) set({ username: suggest(e.target.value) });
            }} />
          <Field id="username" label="USERNAME" value={form.username}
            hint="What they type to sign in. Lower case, no spaces."
            onChange={e => { setTouchedUsername(true); set({ username: e.target.value }); }} />
          <Field id="phone" label="PHONE, OPTIONAL" value={form.phone}
            onChange={e => set({ phone: e.target.value })} />
          <Field id="email" label="EMAIL, OPTIONAL" value={form.email}
            onChange={e => set({ email: e.target.value })} />
        </Section>

        <Divider />
        <Section label="ROLE">
          {roles.map(r => (
            <label key={r.name} style={{
              display: 'flex', gap: 10, padding: 12, cursor: 'pointer',
              border: '1px solid ' + (form.role === r.name ? 'var(--color-accent-default)' : 'var(--color-border-default)'),
              background: form.role === r.name ? 'var(--color-accent-subtle)' : 'transparent',
              borderRadius: 'var(--radius-md)'
            }}>
              <input type="radio" name="role" checked={form.role === r.name}
                onChange={() => set({ role: r.name })} style={{ marginTop: 3 }} />
              <span>
                <span className="t-body-m">{r.label}{r.subtitle ? `, ${r.subtitle}` : ''}</span>
                <span className="t-body-s muted" style={{ display: 'block' }}>{r.blurb}</span>
              </span>
            </label>
          ))}
        </Section>

        {needsBusinesses && (
          <>
            <Divider />
            <Section label="WHICH BUSINESSES">
              <div className="t-body-s muted">
                Only the ones you hold yourself are listed. An admin is not listed here at
                all: they hold all six by rung.
              </div>
              {meta.grantable.map(b => (
                <label key={b.slug} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '4px 0', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.businesses.includes(b.slug)}
                    onChange={e => set({
                      businesses: e.target.checked
                        ? [...form.businesses, b.slug]
                        : form.businesses.filter(s => s !== b.slug)
                    })} />
                  <span className="t-body-m">{b.short_name}</span>
                </label>
              ))}
            </Section>
          </>
        )}

        <Divider />
        <Section label="TWO FACTOR">
          {form.role === 'cashier' ? (
            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.mfa} onChange={e => set({ mfa: e.target.checked })} style={{ marginTop: 4 }} />
              <span>
                <span className="t-body-m">Ask for a code from an app as well</span>
                <span className="t-body-s muted" style={{ display: 'block' }}>
                  Optional for a cashier signing in on a till in a locked room. Turn it on if they use their own phone.
                </span>
              </span>
            </label>
          ) : (
            <div className="t-body-s secondary">
              On, and not optional at this level. A {form.role} can read what everyone earns.
            </div>
          )}
        </Section>

        <div style={{ flex: 1 }} />
        <Divider />
        <div style={{ padding: 16, display: 'grid', gap: 10 }}>
          {error && <Banner tone="danger">{error}</Banner>}
          <Button type="submit" disabled={!ready}>{busy ? 'Creating' : 'Create the account'}</Button>
        </div>
      </form>
    </Sheet>
  );
}

// ---------------------------------------------------------------- editing

function EditSheet({ person, meta, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: person.name, email: person.email || '', phone: person.phone || '',
    role: person.role, businesses: person.businesses.map(b => b.slug)
  });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = patch => setForm(f => ({ ...f, ...patch }));

  const roles = meta.roles.filter(r => meta.assignable.includes(r.name));
  const needsBusinesses = !meta.holdsAllBusinesses.includes(form.role);
  const changesAccess = form.role !== person.role ||
    form.businesses.join() !== person.businesses.map(b => b.slug).join();

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await api.users.update(person.id, { ...form, businesses: needsBusinesses ? form.businesses : undefined });
      onSaved();
    } catch (err) { setError(readable(err)); setBusy(false); }
  }

  return (
    <Sheet title={person.name} subtitle={`@${person.username}, created ${new Date(person.created_at).toLocaleDateString('en-GB')}${person.created_by_name ? ` by ${person.created_by_name}` : ''}`} onClose={onClose}>
      <form onSubmit={submit} style={{ display: 'contents' }}>
        <Section label="WHO">
          <Field id="e-name" label="FULL NAME" value={form.name} onChange={e => set({ name: e.target.value })} />
          <Field id="e-phone" label="PHONE" value={form.phone} onChange={e => set({ phone: e.target.value })} />
          <Field id="e-email" label="EMAIL" value={form.email} onChange={e => set({ email: e.target.value })} />
        </Section>

        <Divider />
        <Section label="ROLE">
          {roles.map(r => (
            <label key={r.name} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '4px 0', cursor: 'pointer' }}>
              <input type="radio" name="erole" checked={form.role === r.name} onChange={() => set({ role: r.name })} />
              <span className="t-body-m">{r.label}</span>
              <span className="t-body-s muted">{r.blurb}</span>
            </label>
          ))}
        </Section>

        {needsBusinesses && (
          <>
            <Divider />
            <Section label="WHICH BUSINESSES">
              {meta.grantable.map(b => (
                <label key={b.slug} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '4px 0', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.businesses.includes(b.slug)}
                    onChange={e => set({
                      businesses: e.target.checked
                        ? [...form.businesses, b.slug]
                        : form.businesses.filter(s => s !== b.slug)
                    })} />
                  <span className="t-body-m">{b.short_name}</span>
                </label>
              ))}
            </Section>
          </>
        )}

        <div style={{ flex: 1 }} />
        <Divider />
        <div style={{ padding: 16, display: 'grid', gap: 10 }}>
          {changesAccess && (
            <Banner tone="warning">
              This changes what they can reach, so it signs them out everywhere. Anything on
              screen at the till is lost, so do it between clients.
            </Banner>
          )}
          {error && <Banner tone="danger">{error}</Banner>}
          <Button type="submit" disabled={busy || !form.name.trim()}>{busy ? 'Saving' : 'Save'}</Button>
        </div>
      </form>
    </Sheet>
  );
}

// ---------------------------------------------------------------- the handover card

/**
 * Shown once. Nothing stores the password in the clear, so closing this without writing it
 * down means issuing a new one. The card says so rather than letting somebody find out.
 */
function HandoverCard({ card: c, onClose }) {
  const ref = useRef(null);
  const [copied, setCopied] = useState(false);

  const text = [
    `EPMS sign in for ${c.name}`,
    `Address:  http://localhost:5173`,
    `Username: ${c.username}`,
    c.password ? `Password: ${c.password}  (temporary, replace at first sign in)` : null,
    c.totpSecret ? `Authenticator secret: ${c.totpSecret}` : null,
    c.recovery?.length ? `Recovery codes: ${c.recovery.join('  ')}` : null
  ].filter(Boolean).join('\n');

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'grid', placeItems: 'center', padding: 24, background: 'rgba(2,6,23,0.45)' }}>
      <div className="card" style={{ width: 560, maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--elevation-lg)' }}>
        <div ref={ref} style={{ padding: 24, display: 'grid', gap: 16, background: 'var(--color-bg-surface)' }}>
          <div>
            <div className="t-heading-l">{c.title}</div>
            <div className="t-body-m secondary">
              For {c.name}. This is the only time it is shown. Nothing in EPMS can read it back.
            </div>
          </div>

          <div style={{ display: 'grid', gap: 10, padding: 16, background: 'var(--color-bg-subtle)', borderRadius: 'var(--radius-md)' }}>
            <Line label="Username" value={c.username} />
            {c.password && <Line label="Temporary password" value={c.password} big />}
            {c.password && (
              <div className="t-body-s muted">
                EPMS will ask them to replace this the moment they sign in, and nothing else
                opens until they do.
              </div>
            )}
          </div>

          {c.totpSecret && (
            <div style={{ display: 'grid', gap: 10, padding: 16, background: 'var(--color-bg-subtle)', borderRadius: 'var(--radius-md)' }}>
              <div className="t-label-xs muted">TWO FACTOR</div>
              <div className="t-body-s secondary">
                Add this in Google Authenticator, Authy or 1Password, using "enter a setup key".
              </div>
              <Line label="Account" value={c.username} />
              <Line label="Key" value={c.totpSecret.replace(/(.{4})/g, '$1 ').trim()} />
            </div>
          )}

          {c.recovery?.length > 0 && (
            <div style={{ display: 'grid', gap: 8, padding: 16, background: 'var(--color-bg-subtle)', borderRadius: 'var(--radius-md)' }}>
              <div className="t-label-xs muted">RECOVERY CODES, ONE USE EACH</div>
              <div className="t-body-s secondary">For when the phone is flat, lost or at home.</div>
              <div className="money t-money-s" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {c.recovery.map(code => <span key={code}>{code}</span>)}
              </div>
            </div>
          )}
        </div>

        <Divider />
        <div className="no-print" style={{ padding: 16, display: 'flex', gap: 8 }}>
          <Button size="m" variant="secondary"
            onClick={() => { navigator.clipboard?.writeText(text); setCopied(true); }}>
            {copied ? 'Copied' : 'Copy'}
          </Button>
          <Button size="m" variant="secondary"
            onClick={() => printElement(ref.current, { title: `EPMS sign in, ${c.username}` })}>
            Print
          </Button>
          <span style={{ flex: 1 }} />
          <Button size="m" onClick={onClose}>I have handed this over</Button>
        </div>
      </div>
    </div>
  );
}

const Line = ({ label, value, big }) => (
  <div style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
    <span className="t-body-s muted" style={{ width: 150, flex: 'none' }}>{label}</span>
    <span className={big ? 'money t-money-l' : 'money t-money-s'} style={{ userSelect: 'all' }}>{value}</span>
  </div>
);
